import { useQuery } from "react-query";
import { useForm } from "react-hook-form";
import Select from "src/components/Select";
import Radio from "src/components/Radio";
import Checkbox from "src/components/Checkbox";
import PriorAttainmentLevelInfo from "src/components/PriorAttainmentLevelInfo";
import {
  getBenefits,
  getDisabilitiesLearningDifficulties,
  getEmploymentStatuses,
  getEmploymentIntensities,
  getEmploymentLengths,
  getHouseholdSituations,
  getPriorAttainmentLevels,
  getUnemployedLengths,
} from "src/models/fields";
import { setDefaultValues } from "src/utils";

const defaultValues = {
  PriorAttainmentLevelID: "",
  HouseholdSituation1ID: "",
  EmploymentStatusID: "",
  IsSelfEmployed: "",
  EmploymentIntensityID: "",
  LengthOfEmploymentID: "",
  BenefitStatusIndicatorID: "",
  LengthOfUnemploymentID: "",
  LearningDiffOrDisID: "",
  DisabilityCategory1ID: "",
};

const PriorAttainment = ({ data, onSubmit }) => {
  // Data loading
  const benefits = useQuery("benefits", getBenefits);
  const disabilities = useQuery(
    "disabilitiesLearningDifficulties",
    getDisabilitiesLearningDifficulties
  );
  const employmentIntensities = useQuery(
    "employmentIntensities",
    getEmploymentIntensities
  );
  const employmentStatuses = useQuery(
    "employmentStatuses",
    getEmploymentStatuses
  );
  const employmentLengths = useQuery("employmentLengths", getEmploymentLengths);
  const householdSituations = useQuery(
    "householdSituations",
    getHouseholdSituations
  );
  const priorAttainmentLevels = useQuery(
    "priorAttainmentLevels",
    getPriorAttainmentLevels
  );
  const unemployedLengths = useQuery("unemployedLengths", getUnemployedLengths);

  // Form
  const { control, register, handleSubmit, formState, watch } = useForm({
    mode: "all",
    defaultValues: setDefaultValues(defaultValues, data),
  });
  const { errors, isSubmitting } = formState;

  // Watch fields for changes
  const priorAttainmentLevel = watch("PriorAttainmentLevelID");
  const learningDiffOrDis = watch("LearningDiffOrDisID");
  const employmentStatus = watch("EmploymentStatusID");

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <h2>Distance Learning</h2>

      <Select
        label="Please select the level that corresponds to your highest qualification achieved PRIOR to starting this course:"
        name="PriorAttainmentLevelID"
        control={control}
        isLoading={priorAttainmentLevels.isLoading}
        options={priorAttainmentLevels.data}
        validation={{ required: "This field is required" }}
        error={errors.PriorAttainmentLevelID}
      />

      <PriorAttainmentLevelInfo value={priorAttainmentLevel} />

      <h2>Household Details</h2>

      <Select
        label="Please select the statement which applies to your household situation:"
        name="HouseholdSituation1ID"
        control={control}
        isLoading={householdSituations.isLoading}
        options={householdSituations.data}
        validation={{ required: "This field is required" }}
        error={errors.HouseholdSituation1ID}
      />

      <h2>Employment Status</h2>

      <Select
        label="Please chose the statement that best describes your status BEFORE starting the course:"
        name="EmploymentStatusID"
        control={control}
        isLoading={employmentStatuses.isLoading}
        options={employmentStatuses.data}
        validation={{ required: "This field is required" }}
        error={errors.EmploymentStatusID}
      />

      {/* Paid Employment */}
      {employmentStatus === "10" && (
        <>
          <Radio
            label="I am Self Employed"
            name="IsSelfEmployed"
            options={[
              { label: "Yes", value: "1" },
              { label: "No", value: "0" },
            ]}
            inline
            register={register}
            validation={{ required: "This field is required" }}
            error={errors.IsSelfEmployed}
          />

          <Select
            label="I am employed for:"
            name="EmploymentIntensityID"
            control={control}
            isLoading={employmentIntensities.isLoading}
            options={employmentIntensities.data}
            validation={{ required: "This field is required" }}
            error={errors.EmploymentIntensityID}
          />

          <Select
            label="I have been employed for:"
            name="LengthOfEmploymentID"
            control={control}
            isLoading={employmentLengths.isLoading}
            options={employmentLengths.data}
            validation={{ required: "This field is required" }}
            error={errors.LengthOfEmploymentID}
          />
        </>
      )}

      {/* Unemployed looking for work */}
      {employmentStatus === "11" && (
        <Select
          label="I am in receipt of the following benefits:"
          name="BenefitStatusIndicatorID"
          control={control}
          isLoading={benefits.isLoading}
          options={benefits.data}
          validation={{ required: "This field is required" }}
          error={errors.BenefitStatusIndicatorID}
        />
      )}

      {/* Unemployed looking/not looking for work */}
      {(employmentStatus === "11" || employmentStatus === "12") && (
        <Select
          label="I have been unemployed for:"
          name="LengthOfUnemploymentID"
          control={control}
          isLoading={unemployedLengths.isLoading}
          options={unemployedLengths.data}
          validation={{ required: "This field is required" }}
          error={errors.LengthOfUnemploymentID}
        />
      )}

      <h2>Support Services</h2>

      <Radio
        label="Do you consider yourself to have a disability, learning difficulty or health problem?"
        name="LearningDiffOrDisID"
        options={[
          { label: "Yes", value: "1" },
          { label: "No", value: "2" },
        ]}
        inline
        register={register}
        validation={{ required: "This field is required" }}
        error={errors.LearningDiffOrDisID}
      />

      {learningDiffOrDis === "1" && (
        <Select
          label="Please indicate the most significant or primary learning difficulty, disability or health problem that impacts your education:"
          name="DisabilityCategory1ID"
          control={control}
          isLoading={disabilities.isLoading}
          options={disabilities.data}
          validation={{ required: "This field is required" }}
          error={errors.DisabilityCategory1ID}
        />
      )}

      <fieldset className="form-group">
        <legend className="form-label">Do any of the following apply?</legend>

        <Checkbox
          label="Young Asylum Seeker"
          name="YoungAsylumSeeker"
          register={register}
        />
        <Checkbox label="Looked After" name="LookedAfter" register={register} />
        <Checkbox label="Care Leaver" name="CareLeaver" register={register} />
        <Checkbox label="Carer" name="YoungCarer" register={register} />
      </fieldset>

      <button type="submit" className="form-submit" disabled={isSubmitting}>
        {isSubmitting ? "Saving" : "Next"}
      </button>
    </form>
  );
};

export default PriorAttainment;
